<script lang="ts">
  // Activation and reset: password, then TOTP set-up with a first code, then the recovery
  // codes. Nothing is saved before the last request (docs/spec.md, Authentification).
  import { onMount } from 'svelte';
  import QRCode from 'qrcode';
  import { api, ApiError, type Activation } from '../lib/api';
  import { router } from '../lib/router.svelte';

  let { token }: { token: string } = $props();

  let info = $state<Activation | null>(null);
  let step = $state<'password' | 'totp' | 'codes'>('password');
  let password = $state('');
  let confirm = $state('');
  let secret = $state('');
  let qr = $state('');
  let code = $state('');
  let codes = $state<string[]>([]);
  let stored = $state(false);
  let error = $state('');
  let busy = $state(false);
  let fatal = $state('');

  onMount(async () => {
    try {
      info = await api.activation(token);
    } catch (e) {
      fatal = e instanceof ApiError ? e.message : 'Lien invalide';
    }
  });

  async function toTotp() {
    error = '';
    if (!info) return;
    if ([...password].length < info.password_min_length) {
      error = `Le mot de passe doit faire au moins ${info.password_min_length} caractères`;
      return;
    }
    if (password !== confirm) {
      error = 'Les deux mots de passe sont différents';
      return;
    }
    busy = true;
    try {
      const setup = await api.activationTotp(token);
      secret = setup.secret;
      qr = await QRCode.toDataURL(setup.otpauth_url, { margin: 1, width: 200 });
      step = 'totp';
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    } finally {
      busy = false;
    }
  }

  async function finish() {
    error = '';
    busy = true;
    try {
      codes = (await api.activate(token, password, secret, code.trim())).recovery_codes;
      password = confirm = '';
      step = 'codes';
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
      // The password policy is checked by the server at this last step.
      if (e instanceof ApiError && /mot de passe/i.test(e.message)) step = 'password';
    } finally {
      busy = false;
    }
  }

  const secretGroups = $derived(secret.match(/.{1,4}/g)?.join(' ') ?? '');
</script>

<main class="page">
  <section class="card" aria-labelledby="activate-title">
    {#if fatal}
      <h1 id="activate-title">Lien inutilisable</h1>
      <p class="error" role="alert">{fatal}</p>
    {:else if info}
      <h1 id="activate-title">
        {info.kind === 'reset' ? 'Réinitialiser votre accès' : `Bienvenue sur ${info.app_name}`}
      </h1>
      <p class="who">{info.display_name} · {info.email ?? info.username}</p>
      <ol class="steps" aria-label="Étapes">
        <li class:current={step === 'password'}>Mot de passe</li>
        <li class:current={step === 'totp'}>Code à usage unique</li>
        <li class:current={step === 'codes'}>Codes de secours</li>
      </ol>

      {#if step === 'password'}
        <form
          onsubmit={(e) => {
            e.preventDefault();
            void toTotp();
          }}
        >
          <p class="hint">
            Au moins {info.password_min_length} caractères. Une phrase de quelques mots est à la fois sûre et facile
            à retenir ; évitez votre nom, celui de l'entreprise et les années.
          </p>
          <div class="field">
            <label for="pw">Mot de passe</label>
            <input id="pw" class="input" type="password" autocomplete="new-password" required bind:value={password} />
          </div>
          <div class="field">
            <label for="pw2">Confirmation</label>
            <input id="pw2" class="input" type="password" autocomplete="new-password" required bind:value={confirm} />
          </div>
          <button type="submit" class="btn primary" disabled={busy}>Continuer</button>
        </form>
      {:else if step === 'totp'}
        <form
          onsubmit={(e) => {
            e.preventDefault();
            void finish();
          }}
        >
          <p class="hint">
            Scannez ce code avec votre application d'authentification (Google Authenticator, Microsoft
            Authenticator, 1Password, Bitwarden…), puis saisissez le code à 6 chiffres qu'elle affiche.
          </p>
          <img class="qr" src={qr} alt="QR code à scanner avec l'application d'authentification" />
          <p class="hint">Saisie manuelle : <code class="secret">{secretGroups}</code></p>
          <div class="field">
            <label for="code">Code à 6 chiffres</label>
            <input id="code" class="input code" inputmode="numeric" autocomplete="one-time-code" required bind:value={code} />
          </div>
          <button type="submit" class="btn primary" disabled={busy}>Activer</button>
        </form>
      {:else}
        <p>
          Votre compte est prêt. Gardez ces codes de secours en lieu sûr : chacun permet une connexion si vous
          perdez votre téléphone. Ils ne seront plus affichés.
        </p>
        <ul class="codes">
          {#each codes as c (c)}<li><code>{c}</code></li>{/each}
        </ul>
        <label class="check"><input type="checkbox" bind:checked={stored} /> J'ai mis ces codes en lieu sûr</label>
        <button type="button" class="btn primary" disabled={!stored} onclick={() => router.go('/login')}>
          Aller à la connexion
        </button>
      {/if}

      {#if error}<p class="error" role="alert">{error}</p>{/if}
    {:else}
      <p class="hint">Chargement…</p>
    {/if}
  </section>
</main>

<style>
  .page {
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: var(--bg-page);
  }
  .card {
    width: 100%;
    max-width: 460px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
  }
  .who {
    margin: -8px 0 0;
    color: var(--muted);
  }
  .steps {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    gap: 8px;
    font-size: 12px;
    counter-reset: step;
  }
  .steps li {
    flex: 1;
    padding-top: 6px;
    border-top: 3px solid var(--border);
    color: var(--muted);
  }
  .steps li.current {
    border-color: var(--accent);
    color: var(--ink);
    font-weight: 600;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .hint {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
  .qr {
    align-self: center;
    width: 200px;
    height: 200px;
    background: #ffffff;
    padding: 8px;
    border-radius: var(--radius-m);
  }
  .secret {
    font-family: var(--font-mono);
    word-break: break-all;
  }
  .code {
    font-size: 20px;
    letter-spacing: 0.2em;
  }
  .codes {
    margin: 0;
    padding: 16px;
    list-style: none;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    background: var(--bg-page);
    border-radius: var(--radius-m);
    font-family: var(--font-mono);
    font-size: 15px;
  }
  .check {
    display: flex;
    gap: 8px;
    align-items: center;
  }
</style>
